import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ReceiptLong as LedgerIcon,
  FileDownload as ExportIcon,
  Refresh as RefreshIcon,
  TrendingUp as CreditIcon,
  TrendingDown as DebitIcon,
  Functions as NetIcon,
  Tag as CountIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import moment from 'moment-jalaali';
import { toast } from 'react-toastify';
import ProtectedRoute from '../components/common/ProtectedRoute';
import FilterPanel, { ActiveFilter } from '../components/common/FilterPanel';
import JalaliDateField from '../components/common/JalaliDateField';
import ResponsiveTable, { Column } from '../components/common/ResponsiveTable';
import ResponsiveModal from '../components/common/ResponsiveModal';
import financeLedgerService, {
  LedgerRow,
  LedgerSource,
  LedgerDirection,
  LedgerSummary,
  SOURCE_LABELS,
  TYPE_LABELS,
  GATEWAY_LABELS,
  GATEWAY_COLORS,
  GATEWAY_OPTIONS,
  SPLIT_LEG_LABELS,
  STATUS_LABELS,
  DIRECTION_LABELS,
  OWNER_LABELS,
  TYPES_BY_SOURCE,
} from '../services/financeLedgerService';
import './EntityManagement.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const toPersianDigits = (s: string | number): string =>
  String(s).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);

const formatToman = (n: number | null | undefined): string => {
  if (n == null || !isFinite(Number(n))) return '—';
  return `${new Intl.NumberFormat('fa-IR').format(Number(n))} تومان`;
};

const formatJalaliDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    return toPersianDigits(moment(iso).format('jYYYY/jMM/jDD HH:mm'));
  } catch {
    return '—';
  }
};

/** Parse a Jalali date string "1404/01/01" → ISO (start or end of day). */
const jalaliToISO = (value: string, endOfDay = false): string | undefined => {
  const v = value.trim();
  if (!v) return undefined;
  const m = moment(v, 'jYYYY/jMM/jDD');
  if (!m.isValid()) return undefined;
  if (endOfDay) m.endOf('day');
  else m.startOf('day');
  return m.toISOString();
};

const SOURCE_OPTIONS = Object.entries(SOURCE_LABELS) as Array<[LedgerSource, string]>;

const STATUS_OPTIONS = [
  'completed', 'success', 'pending', 'processing', 'approved',
  'failed', 'cancelled', 'rejected', 'waived',
];

interface FiltersState {
  fromJalali: string;
  toJalali: string;
  source: '' | LedgerSource;
  type: string;
  gateway: string;
  direction: '' | LedgerDirection;
  status: string;
  minAmount: string;
  maxAmount: string;
  search: string;
  userQuery: string;
  storeQuery: string;
}

const EMPTY_FILTERS: FiltersState = {
  fromJalali: '',
  toJalali: '',
  source: '',
  type: '',
  gateway: '',
  direction: '',
  status: '',
  minAmount: '',
  maxAmount: '',
  search: '',
  userQuery: '',
  storeQuery: '',
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/** Build the initial filter state from URL query params (drill-down links). */
const filtersFromParams = (sp: URLSearchParams): FiltersState => ({
  ...EMPTY_FILTERS,
  fromJalali: sp.get('from') || '',
  toJalali: sp.get('to') || '',
  source: (sp.get('source') as FiltersState['source']) || '',
  type: sp.get('type') || '',
  gateway: sp.get('gateway') || '',
  direction: (sp.get('direction') as FiltersState['direction']) || '',
  status: sp.get('status') || '',
  search: sp.get('search') || '',
  userQuery: sp.get('userQuery') || '',
  storeQuery: sp.get('storeQuery') || '',
});

const FinanceTransactions: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialFilters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const [filters, setFilters] = useState<FiltersState>(initialFilters);
  const [applied, setApplied] = useState<FiltersState>(initialFilters);
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<LedgerSummary | null>(null);
  const [page, setPage] = useState(0); // 0-based for ResponsiveTable
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState<LedgerRow | null>(null);

  const buildParams = useCallback((f: FiltersState) => ({
    from: jalaliToISO(f.fromJalali),
    to: jalaliToISO(f.toJalali, true),
    sources: f.source ? [f.source] : undefined,
    types: f.type ? [f.type] : undefined,
    gateways: f.gateway ? [f.gateway] : undefined,
    direction: f.direction || undefined,
    statuses: f.status ? [f.status] : undefined,
    minAmount: f.minAmount ? Number(f.minAmount) : undefined,
    maxAmount: f.maxAmount ? Number(f.maxAmount) : undefined,
    search: f.search || undefined,
    userQuery: f.userQuery || undefined,
    storeQuery: f.storeQuery || undefined,
  }), []);

  const fetchData = useCallback(async (f: FiltersState, p: number, rpp: number) => {
    setLoading(true);
    try {
      const params = buildParams(f);
      const [ledger, sum] = await Promise.all([
        financeLedgerService.getLedger({ ...params, page: p + 1, limit: rpp }),
        financeLedgerService.getLedgerSummary(params),
      ]);
      setRows(ledger?.rows || []);
      setTotal(ledger?.total || 0);
      setSummary(sum || null);
    } catch {
      toast.error('خطا در دریافت تراکنش‌ها');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchData(applied, page, rowsPerPage);
  }, [applied, page, rowsPerPage, fetchData]);

  const applyFilters = () => {
    setPage(0);
    setApplied(filters);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(0);
  };

  const setQuickRange = (days: number | 'thisJMonth') => {
    let from: ReturnType<typeof moment>;
    const to = moment();
    if (days === 'thisJMonth') {
      from = moment().startOf('jMonth' as any);
    } else {
      from = moment().subtract(days, 'days');
    }
    const next = {
      ...filters,
      fromJalali: from.format('jYYYY/jMM/jDD'),
      toJalali: to.format('jYYYY/jMM/jDD'),
    };
    setFilters(next);
    setPage(0);
    setApplied(next);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await financeLedgerService.exportLedger(buildParams(applied));
      toast.success('فایل خروجی آماده شد');
    } catch {
      toast.error('خطا در دریافت خروجی — دسترسی «خروجی اکسل مالی» لازم است');
    } finally {
      setExporting(false);
    }
  };

  // active-filter chips
  const activeFilters: ActiveFilter[] = useMemo(() => {
    const chips: ActiveFilter[] = [];
    const remove = (patch: Partial<FiltersState>) => () => {
      const next = { ...applied, ...patch };
      setFilters(next);
      setApplied(next);
      setPage(0);
    };
    if (applied.fromJalali) chips.push({ key: 'from', label: `از ${toPersianDigits(applied.fromJalali)}`, onRemove: remove({ fromJalali: '' }) });
    if (applied.toJalali) chips.push({ key: 'to', label: `تا ${toPersianDigits(applied.toJalali)}`, onRemove: remove({ toJalali: '' }) });
    if (applied.source) chips.push({ key: 'source', label: SOURCE_LABELS[applied.source], onRemove: remove({ source: '', type: '' }) });
    if (applied.type) chips.push({ key: 'type', label: TYPE_LABELS[applied.type] || applied.type, onRemove: remove({ type: '' }) });
    if (applied.gateway) chips.push({ key: 'gateway', label: `درگاه: ${GATEWAY_LABELS[applied.gateway] || applied.gateway}`, onRemove: remove({ gateway: '' }) });
    if (applied.direction) chips.push({ key: 'dir', label: DIRECTION_LABELS[applied.direction], onRemove: remove({ direction: '' }) });
    if (applied.status) chips.push({ key: 'status', label: STATUS_LABELS[applied.status] || applied.status, onRemove: remove({ status: '' }) });
    if (applied.minAmount) chips.push({ key: 'min', label: `حداقل ${formatToman(Number(applied.minAmount))}`, onRemove: remove({ minAmount: '' }) });
    if (applied.maxAmount) chips.push({ key: 'max', label: `حداکثر ${formatToman(Number(applied.maxAmount))}`, onRemove: remove({ maxAmount: '' }) });
    if (applied.search) chips.push({ key: 'q', label: `جستجو: ${applied.search}`, onRemove: remove({ search: '' }) });
    if (applied.userQuery) chips.push({ key: 'user', label: `کاربر: ${applied.userQuery}`, onRemove: remove({ userQuery: '' }) });
    if (applied.storeQuery) chips.push({ key: 'store', label: `فروشگاه: ${applied.storeQuery}`, onRemove: remove({ storeQuery: '' }) });
    return chips;
  }, [applied]);

  const typeOptions = filters.source
    ? TYPES_BY_SOURCE[filters.source]
    : Object.keys(TYPE_LABELS);

  // table columns
  const columns: Column<LedgerRow>[] = [
    {
      id: 'date',
      label: 'تاریخ',
      priority: 1,
      format: (v) => <span style={{ whiteSpace: 'nowrap' }}>{formatJalaliDateTime(v)}</span>,
    },
    {
      id: 'source',
      label: 'منبع',
      priority: 3,
      format: (v: LedgerSource) => (
        <Chip size="small" variant="outlined" label={SOURCE_LABELS[v] || v} />
      ),
    },
    {
      id: 'type',
      label: 'نوع',
      priority: 2,
      format: (v: string) => TYPE_LABELS[v] || v,
    },
    {
      id: 'gateway' as any,
      label: 'درگاه',
      priority: 2,
      format: (_v: any, row) => {
        const g = row.gateway || null;
        const og = row.orderGateway || null;
        if (!g && !og) return <span style={{ color: 'var(--color-text-disabled)' }}>—</span>;
        const key = g || og!;
        const viaOrder = !g && !!og;
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            <Chip
              size="small"
              label={GATEWAY_LABELS[key] || key}
              sx={{ borderColor: GATEWAY_COLORS[key] || undefined, color: GATEWAY_COLORS[key] || undefined, fontWeight: 700 }}
              variant="outlined"
            />
            {(viaOrder || row.orderSplit || row.splitLeg) && (
              <span style={{ display: 'block', fontSize: 10, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                {row.splitLeg ? SPLIT_LEG_LABELS[row.splitLeg] || row.splitLeg : row.orderSplit ? 'سفارش پرداخت ترکیبی' : ''}
                {viaOrder ? `${row.orderSplit || row.splitLeg ? ' · ' : ''}درگاهِ سفارش` : ''}
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'direction',
      label: 'جهتِ حساب',
      priority: 4,
      format: (v: LedgerDirection, row) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <span
            style={{
              color:
                v === 'credit' ? 'var(--color-success-dark)' :
                v === 'debit' ? 'var(--color-error-dark)' : 'var(--color-text-secondary)',
              fontWeight: 700,
            }}
          >
            {DIRECTION_LABELS[v] || v}
          </span>
          <span style={{ display: 'block', fontSize: 10, color: 'var(--color-text-disabled)' }}>
            {OWNER_LABELS[row.source] || ''}
          </span>
        </span>
      ),
    },
    {
      id: 'amount',
      label: 'مبلغ',
      priority: 1,
      format: (v: number, row) => (
        <span
          style={{
            fontWeight: 700,
            whiteSpace: 'nowrap',
            color:
              row.direction === 'credit' ? 'var(--color-success-dark)' :
              row.direction === 'debit' ? 'var(--color-error-dark)' : undefined,
          }}
        >
          {row.direction === 'debit' ? '−' : row.direction === 'credit' ? '+' : ''}
          {formatToman(v)}
        </span>
      ),
    },
    {
      id: 'party',
      label: 'طرف حساب',
      priority: 5,
      hideOnMobile: false,
      format: (_v, row) => {
        const userName = row.user ? [row.user.firstName, row.user.lastName].filter(Boolean).join(' ') : '';
        const parts = [
          userName || (row.user?.phoneNumber ? toPersianDigits(row.user.phoneNumber) : ''),
          row.store?.name,
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(' · ') : '—';
      },
    },
    {
      id: 'status',
      label: 'وضعیت',
      priority: 6,
      format: (v: string) => (
        <Chip
          size="small"
          label={STATUS_LABELS[v] || v}
          color={
            ['completed', 'success', 'paid', 'approved'].includes(v) ? 'success' :
            ['failed', 'cancelled', 'rejected'].includes(v) ? 'error' :
            ['pending', 'processing'].includes(v) ? 'warning' : 'default'
          }
          variant="outlined"
        />
      ),
    },
    {
      id: 'refId',
      label: 'کد پیگیری',
      hideOnMobile: true,
      detail: true,
      format: (v: string | null) => (v ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> : '—'),
    },
  ];

  // AUDIT M-12: summary totals only count rows where money actually moved
  // (completed/success/paid + approved penalties) — pending/rejected rows are
  // visible in the table but excluded from these numbers.
  const singleSourceSelected = !!applied.source;
  const summaryCards = [
    { title: 'جمع بستانکار (جابه‌جاشده)', value: summary?.credit, icon: <CreditIcon />, color: 'green' },
    { title: 'جمع بدهکار (جابه‌جاشده)', value: summary?.debit, icon: <DebitIcon />, color: 'red' },
    // AUDIT CRIT-6: a cross-source "net" is not an accounting figure (one event can
    // legitimately appear in 2+ sources). Show خالص only for a single-source filter.
    ...(singleSourceSelected
      ? [{ title: 'خالص این منبع', value: summary?.net, icon: <NetIcon />, color: 'blue' }]
      : []),
    { title: 'تعداد اسناد مؤثر', value: summary?.count, icon: <CountIcon />, color: 'purple', isCount: true },
  ];

  return (
    <ProtectedRoute requiredPermissions={['admin_finance_ledger']}>
      <Helmet>
        <title>دفتر تراکنش‌های مالی</title>
      </Helmet>

      <div className="entity-page" dir="rtl">
        {/* Header */}
        <div className="entity-header">
          <div>
            <h1 className="entity-title">
              <LedgerIcon style={{ verticalAlign: 'middle', marginLeft: 8 }} />
              دفتر تراکنش‌های مالی
            </h1>
            <p className="entity-subtitle">تمام جابه‌جایی‌های پول پلتفرم — قابل جستجو و فیلتر، با خروجی اکسل</p>
          </div>
          <div className="header-actions">
            <Button
              variant="outlined"
              size="small"
              startIcon={exporting ? <CircularProgress size={14} /> : <ExportIcon />}
              onClick={handleExport}
              disabled={exporting || loading}
            >
              خروجی اکسل
            </Button>
            <button
              className="header-btn refresh"
              onClick={() => fetchData(applied, page, rowsPerPage)}
              disabled={loading}
              title="بروزرسانی"
            >
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </button>
          </div>
        </div>

        {/* Summary bar (live for active filter) */}
        <div className="entity-stats">
          {summaryCards.map((c, i) => (
            <div key={i} className="stat-card">
              <div className={`stat-icon ${c.color}`}>{c.icon}</div>
              <div className="stat-content">
                <div className="stat-label">{c.title}</div>
                <div className="stat-value">
                  {c.value == null
                    ? '—'
                    : (c as any).isCount
                      ? new Intl.NumberFormat('fa-IR').format(c.value)
                      : formatToman(c.value)}
                </div>
              </div>
            </div>
          ))}
        </div>

        <p style={{ fontSize: 11, color: 'var(--color-text-secondary)', margin: '4px 0 12px' }}>
          ℹ️ «بستانکار/بدهکار» هر سند نسبت به <b>حساب صاحب همان سند</b> است (ستون جهت، نام حساب را نشان می‌دهد).
          مثلاً یک بازگشت وجه دو سند دارد: بدهکارِ «حساب فروشنده» و بستانکارِ «کیف‌پول خریدار» — این یعنی پول از فروشنده به خریدار منتقل شده، نه اینکه پلتفرم ضرر کرده باشد.
          جمع‌های بالا فقط اسنادِ «پول جابه‌جاشده» را می‌شمارند (درخواست‌های در انتظار/ردشده شمرده نمی‌شوند) و چون یک رویداد می‌تواند در چند منبع سند داشته باشد، «خالص» فقط وقتی نمایش داده می‌شود که یک منبع را فیلتر کرده باشید.
        </p>

        {/* Quick ranges */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Button size="small" variant="text" onClick={() => setQuickRange(0)}>امروز</Button>
          <Button size="small" variant="text" onClick={() => setQuickRange(7)}>۷ روز اخیر</Button>
          <Button size="small" variant="text" onClick={() => setQuickRange(30)}>۳۰ روز اخیر</Button>
          <Button size="small" variant="text" onClick={() => setQuickRange('thisJMonth')}>این ماه شمسی</Button>
        </Box>

        {/* Filters */}
        <FilterPanel
          activeFilters={activeFilters}
          onApply={applyFilters}
          onClear={clearFilters}
          searchSlot={
            <TextField
              fullWidth
              size="small"
              placeholder="جستجو: کد پیگیری، شماره سفارش، شرح..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            />
          }
        >
          <JalaliDateField
            label="از تاریخ (شمسی)"
            value={filters.fromJalali}
            onChange={(v) => setFilters({ ...filters, fromJalali: v })}
          />
          <JalaliDateField
            label="تا تاریخ (شمسی)"
            value={filters.toJalali}
            onChange={(v) => setFilters({ ...filters, toJalali: v })}
          />
          <TextField
            select
            size="small"
            label="منبع"
            value={filters.source}
            onChange={(e) => setFilters({ ...filters, source: e.target.value as any, type: '' })}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">همه</MenuItem>
            {SOURCE_OPTIONS.map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="نوع تراکنش"
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">همه</MenuItem>
            {typeOptions.map((t) => (
              <MenuItem key={t} value={t}>{TYPE_LABELS[t] || t}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="درگاه"
            value={filters.gateway}
            onChange={(e) => setFilters({ ...filters, gateway: e.target.value })}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">همه</MenuItem>
            {GATEWAY_OPTIONS.map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="جهت"
            value={filters.direction}
            onChange={(e) => setFilters({ ...filters, direction: e.target.value as any })}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">همه</MenuItem>
            <MenuItem value="credit">بستانکار</MenuItem>
            <MenuItem value="debit">بدهکار</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label="وضعیت"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">همه</MenuItem>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>{STATUS_LABELS[s] || s}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="مبلغ از (تومان)"
            type="number"
            value={filters.minAmount}
            onChange={(e) => setFilters({ ...filters, minAmount: e.target.value })}
            sx={{ minWidth: 130 }}
          />
          <TextField
            size="small"
            label="مبلغ تا (تومان)"
            type="number"
            value={filters.maxAmount}
            onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value })}
            sx={{ minWidth: 130 }}
          />
          <TextField
            size="small"
            label="کاربر (موبایل/نام)"
            value={filters.userQuery}
            onChange={(e) => setFilters({ ...filters, userQuery: e.target.value })}
            sx={{ minWidth: 160 }}
          />
          <TextField
            size="small"
            label="فروشگاه (نام)"
            value={filters.storeQuery}
            onChange={(e) => setFilters({ ...filters, storeQuery: e.target.value })}
            sx={{ minWidth: 160 }}
          />
        </FilterPanel>

        {/* Table */}
        <ResponsiveTable<LedgerRow & { _key: string }>
          columns={columns as any}
          data={rows.map((r, i) => ({ ...r, _key: `${r.source}-${r.docId}-${r.txId || i}-${page}` }))}
          keyField="_key"
          loading={loading}
          emptyMessage="تراکنشی با این فیلترها پیدا نشد"
          onRowClick={(row) => setDetail(row)}
          pagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={total}
          onPageChange={setPage}
          onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
          stickyHeader
        />

        {/* Breakdown by source */}
        {summary && summary.bySource.length > 0 && (
          <div className="edit-card" style={{ marginTop: 16 }}>
            <div className="edit-card-header">تفکیک بر اساس منبع (برای فیلتر فعال)</div>
            <div className="edit-card-body">
              <table className="entity-table">
                <thead>
                  <tr>
                    <th>منبع</th>
                    <th>بستانکار</th>
                    <th>بدهکار</th>
                    <th>خالص</th>
                    <th>تعداد</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.bySource.map((s) => (
                    <tr key={s.source}>
                      <td>{SOURCE_LABELS[s.source] || s.source}</td>
                      <td style={{ color: 'var(--color-success-dark)' }}>{formatToman(s.credit)}</td>
                      <td style={{ color: 'var(--color-error-dark)' }}>{formatToman(s.debit)}</td>
                      <td style={{ fontWeight: 700 }}>{formatToman(s.credit - s.debit)}</td>
                      <td>{new Intl.NumberFormat('fa-IR').format(s.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Detail modal */}
        <ResponsiveModal
          open={!!detail}
          onClose={() => setDetail(null)}
          title="جزئیات سند مالی"
          icon={<LedgerIcon />}
          maxWidth="sm"
        >
          {detail && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {[
                ['تاریخ', formatJalaliDateTime(detail.date)],
                ['منبع', SOURCE_LABELS[detail.source] || detail.source],
                ['صاحب حساب این سند', OWNER_LABELS[detail.source] || '—'],
                ['نوع', TYPE_LABELS[detail.type] || detail.type],
                [
                  'جهت (نسبت به صاحب حساب)',
                  `${DIRECTION_LABELS[detail.direction] || detail.direction}${
                    detail.type === 'refund'
                      ? detail.source === 'user_wallet'
                        ? ' — واریز رفاند به کیف‌پول خریدار؛ سند بدهکار متناظر در «حساب فروشنده» ثبت شده'
                        : detail.source === 'seller_balance'
                          ? ' — کسر رفاند از فروشنده؛ سند بستانکار متناظر در «کیف‌پول خریدار» ثبت شده'
                          : ''
                      : ''
                  }`,
                ],
                ['مبلغ', formatToman(detail.amount)],
                [
                  'درگاه / کانال پول',
                  detail.gateway || detail.orderGateway
                    ? `${GATEWAY_LABELS[(detail.gateway || detail.orderGateway) as string] || detail.gateway || detail.orderGateway}${!detail.gateway && detail.orderGateway ? ' (درگاه سفارش مرتبط)' : ''}${detail.orderGateway && detail.gateway && detail.gateway !== detail.orderGateway ? ` — سفارش با ${GATEWAY_LABELS[detail.orderGateway] || detail.orderGateway}` : ''}${detail.orderSplit ? ' · سفارش پرداخت ترکیبی' : ''}${detail.splitLeg ? ` · ${SPLIT_LEG_LABELS[detail.splitLeg] || detail.splitLeg}` : ''}${detail.surchargeAmount ? ` · شامل کارمزد اقساطی ${formatToman(detail.surchargeAmount)}` : ''}`
                    : '—',
                ],
                ['وضعیت', STATUS_LABELS[detail.status] || detail.status],
                ['شرح', detail.description || '—'],
                [
                  'کاربر',
                  detail.user
                    ? `${[detail.user.firstName, detail.user.lastName].filter(Boolean).join(' ')} ${detail.user.phoneNumber ? `(${toPersianDigits(detail.user.phoneNumber)})` : ''}`
                    : '—',
                ],
                ['فروشگاه', detail.store?.name || '—'],
                ['شماره سفارش/سند', detail.orderNumber || (detail.orderId ? String(detail.orderId) : '—')],
                ['کد پیگیری', detail.refId || '—'],
                ['شناسه سند', detail.docId ? String(detail.docId) : '—'],
              ].map(([label, value]) => (
                <Box
                  key={label as string}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    borderBottom: '1px dashed var(--color-border, #eee)',
                    pb: 0.75,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left', wordBreak: 'break-all' }}>
                    {value as React.ReactNode}
                  </Typography>
                </Box>
              ))}
              {detail.userId && (
                <Tooltip title="گردش حساب این کاربر">
                  <Button
                    size="small"
                    variant="outlined"
                    href={`/finance/statements?userId=${detail.userId}`}
                  >
                    مشاهده گردش حساب کاربر
                  </Button>
                </Tooltip>
              )}
              {detail.storeId && (
                <Tooltip title="گردش حساب این فروشگاه">
                  <Button
                    size="small"
                    variant="outlined"
                    href={`/finance/statements?storeId=${detail.storeId}`}
                  >
                    مشاهده گردش حساب فروشگاه
                  </Button>
                </Tooltip>
              )}
            </Box>
          )}
        </ResponsiveModal>
      </div>
    </ProtectedRoute>
  );
};

export default FinanceTransactions;
